import { LoggerWithoutDebug, Wllama, type LoadedContextInfo } from "@wllama/wllama/esm";

import { getOpfsPathForManifestFile } from "@/install/install-flow";
import type { LaneManifest } from "@/runtime/events";
import { readOpfsFile } from "@/storage/opfs";

const QWEN_PROMPT_MESSAGES = [
  {
    role: "system" as const,
    content: "You are a precise assistant. Reply with digits only.",
  },
  {
    role: "user" as const,
    content: "What is 7 + 5?",
  },
];

const QWEN_PROMPT_LABEL = 'Prompt: "What is 7 + 5?" -> contains "12"';

const WLLAMA_PATHS = {
  "single-thread/wllama.wasm": "/wllama/single-thread/wllama.wasm",
  "multi-thread/wllama.wasm": "/wllama/multi-thread/wllama.wasm",
} as const;

const QWEN_LOAD_CONFIG = {
  seed: 7,
  n_ctx: 512,
  n_batch: 64,
  n_threads: 1,
  offload_kqv: false,
} as const;

const QWEN_SAMPLING = {
  temp: 0,
  top_k: 1,
  top_p: 1,
  penalty_repeat: 1,
  penalty_freq: 0,
  penalty_present: 0,
} as const;

export interface QwenPromptCheckResult {
  durationMs: number;
  outputText: string;
  promptLabel: string;
  responseValid: boolean;
  normalizedDigits: string;
  context: LoadedContextInfo;
  threads: number;
  multithread: boolean;
}

async function getManifestFiles(manifest: LaneManifest): Promise<File[]> {
  return Promise.all(
    manifest.files.map((file) => readOpfsFile(getOpfsPathForManifestFile(manifest, file.path))),
  );
}

function normalizeDigitsOnly(text: string): string {
  return text.replace(/[^\d-]+/g, "");
}

// SmolLM2-135M (the local LLM as of 2026-04-27) replies in prose, e.g.
// "7 + 5 = 12." — the strict digits-only equality check we used for
// Qwen would fail on the concatenation "7512". Match a standalone "12"
// instead so any reply that contains the right answer as a discrete
// number passes.
function containsAnswerNumber(text: string, answer: string): boolean {
  return new RegExp(`(?<!\\d)${answer}(?!\\d)`).test(text);
}

export function getQwenSmokeProbeLabel(): string {
  return QWEN_PROMPT_LABEL;
}

export async function runQwenPromptCheck(
  manifest: LaneManifest,
): Promise<QwenPromptCheckResult> {
  const files = await getManifestFiles(manifest);
  const runtime = new Wllama(WLLAMA_PATHS, {
    logger: LoggerWithoutDebug,
    allowOffline: true,
  });
  const startedAt = performance.now();

  try {
    await runtime.loadModel(files, QWEN_LOAD_CONFIG);
    const context = runtime.getLoadedContextInfo();
    const outputText = await runtime.createChatCompletion(QWEN_PROMPT_MESSAGES, {
      nPredict: 12,
      useCache: false,
      sampling: QWEN_SAMPLING,
    });
    const trimmed = outputText.trim();
    const normalizedDigits = normalizeDigitsOnly(trimmed);
    const responseValid = containsAnswerNumber(trimmed, "12");

    return {
      durationMs: Math.round(performance.now() - startedAt),
      outputText: trimmed,
      promptLabel: QWEN_PROMPT_LABEL,
      responseValid,
      normalizedDigits,
      context,
      threads: runtime.getNumThreads(),
      multithread: runtime.isMultithread(),
    };
  } finally {
    await runtime.exit().catch(() => undefined);
  }
}
